import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';

export interface TableSkeletonColumn {
  header?: string;
  width?: string;
  fitContent?: boolean;
  align?: 'left' | 'center' | 'right';
  type?: 'text' | 'badge' | 'progress' | 'icon' | 'text-subtitle' | 'date';
}

export interface TableSkeletonConfig {
  /** Number of skeleton rows to display */
  rows?: number;
  /** Number of skeleton columns to display */
  columns?: number;
  /** Whether to show the table header skeleton */
  showHeader?: boolean;
  /** Whether to apply row banding (alternate colors) */
  banding?: boolean;
  /** Column definitions with headers */
  columnDefs?: TableSkeletonColumn[];
}

@Component({
  selector: 'vs-table-skeleton',
  templateUrl: './table-skeleton.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  styles: [
    `
      @keyframes shimmer {
        0% {
          background-position: -1000px 0;
        }
        100% {
          background-position: 1000px 0;
        }
      }

      .skeleton-shimmer {
        animation: shimmer 2s infinite linear;
        background: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
        background-size: 1000px 100%;
      }

      .skeleton-shimmer-dark {
        animation: shimmer 2s infinite linear;
        background: linear-gradient(to right, #e8e8e8 0%, #d8d8d8 20%, #e8e8e8 40%, #e8e8e8 100%);
        background-size: 1000px 100%;
      }
    `,
  ],
})
export class TableSkeletonComponent {
  @HostBinding('class') private get _classes() {
    return 'block overflow-hidden';
  }

  @Input() rows = 5;
  @Input() columns = 3;
  @Input() showHeader = true;
  @Input() banding = true;
  @Input() columnDefs?: TableSkeletonColumn[];

  get rowArray(): number[] {
    return Array(this.rows).fill(0);
  }

  get columnArray(): number[] {
    if (this.columnDefs && this.columnDefs.length > 0) {
      return Array(this.columnDefs.length).fill(0);
    }
    return Array(this.columns).fill(0);
  }

  getColumnDef(index: number): TableSkeletonColumn | undefined {
    return this.columnDefs?.[index];
  }

  getColumnWidth(index: number): string {
    const colDef = this.getColumnDef(index);

    // Use defined width if available
    if (colDef?.width) {
      return colDef.width;
    }

    // Use fit-content for small columns (action icons, etc.)
    if (colDef?.fitContent) {
      return 'w-px flex-shrink-0';
    }

    // Use flex-1 for regular columns to match header distribution
    return 'flex-1 min-w-0';
  }

  getColumnAlign(index: number): string {
    const colDef = this.getColumnDef(index);
    const align = colDef?.align || 'left';

    if (align === 'center') return 'justify-center';
    if (align === 'right') return 'justify-end';
    return 'justify-start';
  }

  getSkeletonWidth(rowIndex: number, colIndex: number): number {
    // Add variety to skeleton widths for more realistic appearance
    // Use row and column index to create consistent but varied widths
    // Prime multipliers (7 and 11) create a simple hash that distributes
    // seeds evenly across widths array while avoiding obvious patterns
    const seed = (rowIndex * 7 + colIndex * 11) % 4;
    const widths = [75, 85, 90, 95];
    return widths[seed];
  }

  getColumnType(index: number): string {
    return this.getColumnDef(index)?.type || 'text';
  }

  shouldShowSubtitle(index: number): boolean {
    return this.getColumnType(index) === 'text-subtitle';
  }

  shouldShowBadge(index: number): boolean {
    return this.getColumnType(index) === 'badge';
  }

  shouldShowProgress(index: number): boolean {
    return this.getColumnType(index) === 'progress';
  }

  shouldShowIcon(index: number): boolean {
    return this.getColumnType(index) === 'icon';
  }

  shouldShowDate(index: number): boolean {
    return this.getColumnType(index) === 'date';
  }

  getHeaderSkeletonWidth(index: number): number {
    // Vary header skeleton widths for more realistic appearance
    const seed = index * 13;
    const widths = [60, 70, 65, 75];
    return widths[seed % widths.length];
  }
}
